import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { ClinicSettings } from '../types';

const API_BASE = '/api/v1';

const defaultSettings: ClinicSettings = {
    clinicName: '',
    doctorName: '',
    clinicAddress: '',
    clinicPhone: '',
    clinicEmail: '',
    clinicWebsite: '',
    clinicLogoBase64: undefined,
    clinicLogoMimeType: undefined,
    accentColor: undefined,
};

interface ClinicSettingsContextType {
    settings: ClinicSettings;
    updateSettings: (settings: Partial<ClinicSettings>) => Promise<void>;
    hasCustomSettings: boolean;
    isLoading: boolean;
    error: string | null;
    refreshSettings: () => Promise<void>;
}

const ClinicSettingsContext = createContext<ClinicSettingsContextType>({
    settings: defaultSettings,
    updateSettings: async () => { },
    hasCustomSettings: false,
    isLoading: false,
    error: null,
    refreshSettings: async () => { },
});

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

// Check if user is authenticated
const isAuthenticated = (): boolean => {
    return !!localStorage.getItem('accessToken');
};

export const ClinicSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<ClinicSettings>(() => {
        try {
            const saved = localStorage.getItem('futurasmile_clinic_settings');
            return saved ? JSON.parse(saved) : defaultSettings;
        } catch {
            return defaultSettings;
        }
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch settings from backend
    const refreshSettings = useCallback(async () => {
        if (!isAuthenticated()) return;

        setIsLoading(true);
        setError(null);

        try {
            const res = await fetch(`${API_BASE}/clinic-settings`, {
                method: 'GET',
                credentials: 'include',
                headers: getAuthHeaders(),
            });

            if (res.ok) {
                const data = await res.json();
                setSettings(data);
                // Also update localStorage as cache
                localStorage.setItem('futurasmile_clinic_settings', JSON.stringify(data));
            } else if (res.status !== 401) {
                // Don't treat 401 as error (just means not logged in)
                console.warn('Failed to fetch clinic settings:', res.status);
            }
        } catch (e) {
            console.error('Error fetching clinic settings:', e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load settings on mount if authenticated
    useEffect(() => {
        if (isAuthenticated()) {
            refreshSettings();
        }
    }, [refreshSettings]);

    // Update settings (saves to backend AND localStorage)
    const updateSettings = useCallback(async (newSettings: Partial<ClinicSettings>) => {
        const updated = { ...settings, ...newSettings };

        // Update local state immediately
        setSettings(updated);

        // Always update localStorage
        try {
            localStorage.setItem('futurasmile_clinic_settings', JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to save to localStorage:', e);
        }

        // If authenticated, sync to backend
        if (isAuthenticated()) {
            setIsLoading(true);
            setError(null);

            try {
                const res = await fetch(`${API_BASE}/clinic-settings`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(updated),
                });

                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(data.message || 'Failed to save settings');
                }
            } catch (e: any) {
                setError(e.message || 'Failed to save settings');
                console.error('Error saving clinic settings:', e);
                throw e;
            } finally {
                setIsLoading(false);
            }
        }
    }, [settings]);

    const hasCustomSettings = Boolean(settings.clinicName || settings.doctorName || settings.clinicLogoBase64);

    return (
        <ClinicSettingsContext.Provider value={{ settings, updateSettings, hasCustomSettings, isLoading, error, refreshSettings }}>
            {children}
        </ClinicSettingsContext.Provider>
    );
};

export const useClinicSettings = () => useContext(ClinicSettingsContext);

export default ClinicSettingsContext;
